import React, { useEffect, useState, useContext, useMemo } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Container, Typography, Box, CircularProgress, Alert, Button, TextField, Badge, IconButton, Stack, Tooltip, Autocomplete, InputAdornment } from "@mui/material";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { API_ENDPOINTS } from "../config/api.config";
import { WORKING_DAYS_CONFIG } from "../config/local.config";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip as RechartsTooltip } from 'recharts';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import { useLocation } from 'react-router-dom';
import { UserContext } from '../components/MiniDrawer';
import { GlobalStyles } from '@mui/material';
import { Tooltip as MuiTooltip } from '@mui/material';
import { parseLocalDate, formatDateYYYYMMDD } from '../utils/dateHelpers';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import Chip from '@mui/material/Chip';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Snackbar from '@mui/material/Snackbar';
import { getAbsences, addAbsences, deleteAbsence } from '../utils/absenceService';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

export default function DailyTracking() {
  const { selectedUser, backendUser } = useContext(UserContext);
  const location = useLocation();
  // Leggi la data dalla query string se presente
  const params = new URLSearchParams(location.search);
  const queryDate = params.get('date');
  // Fix timezone: parseLocalDate interpreta la data come locale, non UTC
  const initialDate = queryDate ? parseLocalDate(queryDate) : new Date();
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    user: '',
    project: '',
    activity: '',
    status: '',
    effort_type: '',
    effort: '',
    ticket_id: '',
    description: '',
  });
  const [projects, setProjects] = useState([]);
  const [activities, setActivities] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [users, setUsers] = useState([]);
  const [effortTypes, setEffortTypes] = useState([]);
  const [open, setOpen] = useState(false);
  // Stato per il dialog multiplo
  const [openMultiDialog, setOpenMultiDialog] = useState(false);
  const [multiCommon, setMultiCommon] = useState({ user: '', project: '', activity: '', effort_type: '', status: '' });
  const [multiRows, setMultiRows] = useState([
    { ticket_id: '', effort: '', status: '', description: '' }
  ]);
  const [multiSaving, setMultiSaving] = useState(false);
  const [multiError, setMultiError] = useState(null);
  // Attività abilitate per progetto (single e multi dialog)
  const [allowedActivitiesByProject, setAllowedActivitiesByProject] = useState({ single: [], multi: [] });
  // Stato per il warning dialog quando l'effort supera 8 ore
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [pendingWorklogData, setPendingWorklogData] = useState(null);
  // Stato per il mese visualizzato
  const [viewedMonth, setViewedMonth] = useState(() => {
    const d = selectedDate || new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  // Stato per i giorni con worklog (summary API)
  const [worklogSummary, setWorklogSummary] = useState([]);
  // Stato assenze
  const [absenceDays, setAbsenceDays] = useState([]);
  const [openAbsenceDialog, setOpenAbsenceDialog] = useState(false);
  const [absDialogNavDate, setAbsDialogNavDate] = useState(new Date());
  const [selectedAbsenceDates, setSelectedAbsenceDates] = useState(new Set());
  const [absenceSaving, setAbsenceSaving] = useState(false);
  const [dialogAbsenceDays, setDialogAbsenceDays] = useState([]);
  const [absenceDatesToRemove, setAbsenceDatesToRemove] = useState(new Set());
  const [absenceFullDay, setAbsenceFullDay] = useState(true);
  const [absenceWarningOpen, setAbsenceWarningOpen] = useState(false);

  // Funzione per formattare la data in yyyy-mm-dd
  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${month}-${day}`;
  };

  // Funzione per pulire il ticket_id rimuovendo spazi prima/dopo e tra prefix e resto
  const cleanTicketId = (ticketId, prefix) => {
    if (!ticketId) return '';
    const trimmed = String(ticketId).trim();
    if (!prefix) return trimmed;
    const prefixTrimmed = String(prefix).trim();
    // Se il ticket_id inizia con il prefix, rimuovi gli spazi tra prefix e resto
    if (prefixTrimmed && trimmed.startsWith(prefixTrimmed)) {
      const afterPrefix = trimmed.slice(prefixTrimmed.length).trim();
      return prefixTrimmed + afterPrefix;
    }
    return trimmed;
  };

  // ===== Project Activities per filtrare Activity per progetto =====
  const fetchAllowedActivityIdsForProject = async (projectId) => {
    if (!projectId) return [];
    try {
      const resp = await fetch(`${API_ENDPOINTS.PROJECT_ACTIVITIES}?project_id=${projectId}`);
      if (!resp.ok) return [];
      const data = await resp.json();
      const ids = Array.isArray(data.activity_ids) ? data.activity_ids : [];
      return ids;
    } catch {
      return [];
    }
  };

  // Funzione per validare e convertire effort da minuti (solo numeri)
  const parseEffortToMinutes = (effortInput) => {
    if (!effortInput || typeof effortInput !== 'string') return { minutes: 0, isValid: true, error: null };
    
    const input = effortInput.trim();
    if (input === '') return { minutes: 0, isValid: true, error: null };
    
    // Controlla se ci sono caratteri alfabetici (NON ammessi)
    const invalidChars = input.match(/[a-zA-Z]/g);
    if (invalidChars) {
      return { 
        minutes: 0, 
        isValid: false, 
        error: 'Invalid format. Only numbers are allowed (e.g., 30, 60, 120)' 
      };
    }
    
    // Pattern per solo numeri (es. "20", "45", "120") - trattati come minuti
    const numberOnlyPattern = /^(\d+)$/;
    const numberOnlyMatch = input.match(numberOnlyPattern);
    if (numberOnlyMatch) {
      const minutes = parseInt(numberOnlyMatch[1]);
      if (minutes > 1440) { // 24 ore in minuti
        return { 
          minutes: 0, 
          isValid: false, 
          error: 'Minutes cannot exceed 1440 (24 hours)' 
        };
      }
      return { minutes, isValid: true, error: null };
    }
    
    // Se non matcha nessun pattern valido
    return { 
      minutes: 0, 
      isValid: false, 
      error: 'Invalid format. Enter minutes as a number (e.g., 30, 60, 120)' 
    };
  };

  // Funzione per convertire minuti in formato ore:minuti per display
  const formatMinutesToHoursMinutes = (minutes) => {
    if (!minutes || minutes === 0) return '';
    
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours > 0 && mins > 0) {
      return `${hours}h ${mins}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${mins}m`;
    }
  };

  // Funzione per filtrare i progetti in base ai criteri richiesti
  const filterProjectsForUser = async (currentUser, selectedDate) => {
    if (!currentUser || !selectedDate) {
      return [];
    }
    
    try {
      // Ottieni direttamente i progetti dell'utente usando l'endpoint specifico
      const projectUsersUrl = `${API_ENDPOINTS.PROJECT_USERS}?user_id=${currentUser.id}`;
      
      const projectUsersResponse = await fetch(projectUsersUrl);
      
      if (!projectUsersResponse.ok) {
        console.error('Project-users API error:', projectUsersResponse.status);
        return [];
      }
      
      const projectUsersData = await projectUsersResponse.json();
      
      // Estrai i progetti dalla struttura corretta mantenendo anche validity_end_date
      const userProjectsWithReferences = projectUsersData.user_projects_with_references || [];
      const userProjects = userProjectsWithReferences.map(item => ({
        ...item.project,
        customer: item.customer,
        validity_end_date: item.validity_end_date, // Mantieni il campo validity_end_date
        ticket_id_prefix: item.project.ticket_id_prefix // Mantieni il campo ticket_id_prefix
      }));
      
      // Filtra i progetti per data di validità
      const workDate = formatDate(selectedDate);
      const filteredProjects = userProjects.filter(project => {
        // 1. Il progetto deve essere attivo
        if (!project.active) {
          return false;
        }
        
        // 2. Controlla la data di fine del progetto
        if (project.end_date) {
          // Fix timezone: parseLocalDate interpreta la data come locale
          const projectEndDate = parseLocalDate(project.end_date);
          const selectedDateObj = new Date(selectedDate);
          
          // Normalizza le date per confrontare solo giorno/mese/anno
          const projectEndNormalized = new Date(projectEndDate.getFullYear(), projectEndDate.getMonth(), projectEndDate.getDate());
          const selectedNormalized = new Date(selectedDateObj.getFullYear(), selectedDateObj.getMonth(), selectedDateObj.getDate());
          
          // Se la data selezionata è successiva alla data di fine del progetto, escludi il progetto
          if (selectedNormalized > projectEndNormalized) {
            return false;
          }
        }
        
        // 3. Controlla la data di inizio del progetto (se presente)
        if (project.start_date) {
          // Fix timezone: parseLocalDate interpreta la data come locale
          const projectStartDate = parseLocalDate(project.start_date);
          const selectedDateObj = new Date(selectedDate);
          
          // Normalizza le date per confrontare solo giorno/mese/anno
          const projectStartNormalized = new Date(projectStartDate.getFullYear(), projectStartDate.getMonth(), projectStartDate.getDate());
          const selectedNormalized = new Date(selectedDateObj.getFullYear(), selectedDateObj.getMonth(), selectedDateObj.getDate());
          
          // Se la data selezionata è precedente alla data di inizio del progetto, escludi il progetto
          if (selectedNormalized < projectStartNormalized) {
            return false;
          }
        }
        
        // 4. Controlla la validity_end_date dell'utente per questo progetto
        if (project.validity_end_date) {
          const validityEndDate = new Date(project.validity_end_date);
          const selectedDateObj = new Date(selectedDate);
          
          // Normalizza le date per confrontare solo giorno/mese/anno
          const validityEndNormalized = new Date(validityEndDate.getFullYear(), validityEndDate.getMonth(), validityEndDate.getDate());
          const selectedNormalized = new Date(selectedDateObj.getFullYear(), selectedDateObj.getMonth(), selectedDateObj.getDate());
          
          // Se la data selezionata è successiva alla validity_end_date, escludi il progetto
          if (selectedNormalized > validityEndNormalized) {
            return false;
          }
        }
        
        return true;
      });
      
      return filteredProjects;
    } catch (error) {
      console.error('Error filtering projects:', error);
      return [];
    }
  };



  // Effettua la fetch ogni volta che cambia la data selezionata
  useEffect(() => {
    if (!selectedUser) return;
    const fetchWorklogs = async () => {
      setLoading(true);
      setError(null);
      try {
        const dateStr = formatDate(selectedDate);
        const url = selectedUser === 'ALL'
          ? `${API_ENDPOINTS.WORKLOGS}?date_from=${dateStr}&date_to=${dateStr}`
          : `${API_ENDPOINTS.WORKLOGS}?date_from=${dateStr}&date_to=${dateStr}&user_id=${selectedUser}`;
        console.log('[WORKLOG DEBUG] Fetching:', url);
        const response = await fetch(url);
        if (!response.ok) throw new Error("Failed to fetch worklogs");
        let data = await response.json();
        if (typeof data === 'string') {
          try { data = JSON.parse(data); } catch { data = []; }
        }
        const arr = data.worklogs || [];
        console.log('[WORKLOG DEBUG] Data received:', arr);
        const filtered = arr.filter(wl => {
          let wlDateStr = '';
          if (wl.date) {
            if (typeof wl.date === 'string') {
              wlDateStr = wl.date.slice(0, 10);
            } else if (wl.date instanceof Date) {
              wlDateStr = wl.date.getFullYear() + '-' +
                String(wl.date.getMonth() + 1).padStart(2, '0') + '-' +
                String(wl.date.getDate()).padStart(2, '0');
            } else {
              wlDateStr = String(wl.date).slice(0, 10);
            }
          }
          const match = wlDateStr === dateStr;
          if (!match) console.log('[WORKLOG DEBUG] Excluded:', wl.date, '->', wlDateStr, 'vs', dateStr);
          return match;
        });
        setRows(filtered.map((row, idx) => ({ id: row.id || idx, ...row })));
      } catch (err) {
        setError(err.message);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    fetchWorklogs();
  }, [selectedDate, selectedUser]);

  // Fetch select options on dialog open
  useEffect(() => {
    if (!openDialog) return;
    fetch(API_ENDPOINTS.PROJECTS)
      .then(res => res.json())
      .then(async data => {
        let arr = data.projects || [];
        const allProjects = Array.isArray(arr) ? arr : [];
        
        // Filtra i progetti per l'utente corrente
        console.log('🔍 backendUser available:', !!backendUser, backendUser);
        if (backendUser) {
          const filteredProjects = await filterProjectsForUser(backendUser, selectedDate);
          setProjects(filteredProjects);
        } else {
          console.log('❌ No backendUser available, showing all projects');
          setProjects(allProjects);
        }
      });
    fetch(API_ENDPOINTS.ACTIVITIES)
      .then(res => res.json())
      .then(data => {
        let arr = data.activities || [];
        setActivities(Array.isArray(arr) ? arr.filter(a => String(a.name || '').toUpperCase() !== 'FORECAST') : []);
      });
    fetch(API_ENDPOINTS.STATUS)
      .then(res => res.json())
      .then(data => {
        let statusArr = data.statuses || [];
        setStatuses(Array.isArray(statusArr) ? statusArr : []);
      });
    fetch(API_ENDPOINTS.USERS)
      .then(res => res.json())
      .then(data => {
        let usersArr = data.users || [];
        setUsers(Array.isArray(usersArr) ? usersArr : []);
      });
    fetch(API_ENDPOINTS.EFFORT_TYPES)
      .then(res => res.json())
      .then(data => {
        let effortTypesArr = data.effort_types || [];
        setEffortTypes(Array.isArray(effortTypesArr) ? effortTypesArr : []);
      });
  }, [openDialog, backendUser, selectedDate]);

  // Prevalorizza effort_type con 'Normal (NORM)' quando cambia la lista effortTypes
  useEffect(() => {
    if (effortTypes && effortTypes.length > 0) {
      const defaultEffortType = effortTypes.find(et => et.id === 1 || String(et.id) === '1');
      if (defaultEffortType) {
        // Se il dialog singolo è aperto e non è in modifica, aggiorna il valore
        if (openDialog && !editingRow) {
          setFormData(prev => ({ ...prev, effort_type: defaultEffortType.id }));
        }
        // Se il dialog multiplo è aperto, aggiorna il valore
        if (openMultiDialog) {
          setMultiCommon(prev => ({ ...prev, effort_type: defaultEffortType.id }));
        }
      }
    }
  }, [effortTypes, openDialog, openMultiDialog, editingRow]);

  // Raggruppa per progetto e somma effort (in minuti)
  const projectTotals = {};
  rows.forEach(row => {
    const projectName = row.project && row.project_name ? row.project_name : (row.project_name || 'N/A');
    const effortMinutes = typeof row.effort === 'number' ? row.effort : Number(row.effort);
    if (!projectTotals[projectName]) projectTotals[projectName] = 0;
    if (!isNaN(effortMinutes)) projectTotals[projectName] += effortMinutes;
  });

  // Calcola dati per il pie chart (converte minuti in ore per display)
  const totalEffortMinutes = Object.values(projectTotals).reduce((sum, v) => sum + v, 0);
  const totalEffortHours = totalEffortMinutes / 60;
  const pieData = Object.entries(projectTotals).map(([project, value]) => ({
    name: project,
    value: value / 60, // Converti minuti in ore per display
    percent: totalEffortMinutes ? (value / totalEffortMinutes) * 100 : 0
  }));

  // Calcola il totale ore tracciate per il giorno selezionato
  const totalHours = totalEffortHours;
  let badgeColor = 'success';
  if (totalHours === 0) badgeColor = 'warning';
  else if (totalHours > 0) badgeColor = 'success';

  // Aggiorna viewedMonth anche quando cambia selectedDate (es. al primo render o cambio giorno)
  useEffect(() => {
    if (selectedDate) {
      setViewedMonth(`${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}`);
    }
  }, [selectedDate]);

  // Fetch dei giorni con worklog tramite /user_effort_summary ogni volta che cambia utente o mese visualizzato
  useEffect(() => {
    if (!selectedUser || !viewedMonth) return;
    fetch(`${API_ENDPOINTS.USER_EFFORT_SUMMARY}?user_id=${selectedUser}&year_month=${viewedMonth}`)
      .then(res => res.json())
      .then(data => {
        setWorklogSummary(Array.isArray(data.data) ? data.data : []);
      })
      .catch(() => setWorklogSummary([]));
  }, [selectedUser, viewedMonth]);

  // Carica giorni di ferie per il mese visualizzato
  useEffect(() => {
    if (!selectedUser || !viewedMonth || selectedUser === 'ALL') return;
    getAbsences(selectedUser, viewedMonth).then(setAbsenceDays).catch(() => setAbsenceDays([]));
  }, [selectedUser, viewedMonth]);

  // Carica assenze per il mese navigato nel dialog
  useEffect(() => {
    if (!openAbsenceDialog || !selectedUser || selectedUser === 'ALL' || !absDialogNavDate) return;
    const ym = `${absDialogNavDate.getFullYear()}-${String(absDialogNavDate.getMonth() + 1).padStart(2, '0')}`;
    getAbsences(selectedUser, ym).then(setDialogAbsenceDays).catch(() => setDialogAbsenceDays([]));
  }, [openAbsenceDialog, selectedUser, absDialogNavDate]);

  // Ricarica i progetti filtrati quando cambia la data selezionata
  useEffect(() => {
    if (!openDialog && !openMultiDialog) return;
    
    fetch(API_ENDPOINTS.PROJECTS)
      .then(res => res.json())
      .then(async data => {
        let arr = data.projects || [];
        const allProjects = Array.isArray(arr) ? arr : [];
        
        // Filtra i progetti per l'utente corrente e la nuova data
        console.log('🔍 backendUser available:', !!backendUser, backendUser);
        if (backendUser) {
          const filteredProjects = await filterProjectsForUser(backendUser, selectedDate);
          setProjects(filteredProjects);
          

        } else {
          console.log('❌ No backendUser available, showing all projects');
          setProjects(allProjects);
        }
      });
  }, [selectedDate, backendUser, openDialog, openMultiDialog]);

  // Funzione di utilità per sapere se c'è worklog in un certo giorno
  function hasWorklogForDay(dateStr) {
    const found = worklogSummary.find(item => {
      // Fix timezone: parseLocalDate interpreta la data come locale
      const d = parseLocalDate(item.work_date);
      const itemDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      // Cast a numero
      const effort = Number(item.total_effort);
      
      return itemDate === dateStr && effort > 0;
    });
    return !!found;
  }

  // Funzione per calcolare quanti giorni lavorativi sono passati dall'inizio del mese
  function getWorkingDaysPassedInMonth(firstOfMonth, currentDate) {
    let workingDaysCount = 0;
    const currentDateNormalized = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
    
    // Itera attraverso tutti i giorni dall'inizio del mese fino ad oggi
    for (let day = 1; day <= currentDateNormalized.getDate(); day++) {
      const checkDate = new Date(currentDateNormalized.getFullYear(), currentDateNormalized.getMonth(), day);
      const dayOfWeek = checkDate.getDay();
      
      // Se il giorno è un giorno lavorativo, incrementa il contatore
      if (WORKING_DAYS_CONFIG.workingDaysOfWeek.includes(dayOfWeek)) {
        workingDaysCount++;
      }
    }
    
    return workingDaysCount;
  }

  // Funzione per aggiungere/sottrarre N giorni lavorativi da una data
  function addWorkingDays(startDate, days) {
    const result = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const direction = days > 0 ? 1 : -1;
    let remainingDays = Math.abs(days);
    
    while (remainingDays > 0) {
      result.setDate(result.getDate() + direction);
      const dayOfWeek = result.getDay();
      
      // Se il giorno è un giorno lavorativo, decrementa il contatore
      if (WORKING_DAYS_CONFIG.workingDaysOfWeek.includes(dayOfWeek)) {
        remainingDays--;
      }
    }
    
    return result;
  }

  // Funzione per determinare se siamo entro il periodo di editing del mese precedente
  function isWithinPreviousMonthEditingPeriod() {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    
    // Trova il primo giorno del mese corrente
    const firstOfMonth = new Date(currentYear, currentMonth, 1);
    
    // Calcola quanti giorni lavorativi sono passati dall'inizio del mese
    const workingDaysPassed = getWorkingDaysPassedInMonth(firstOfMonth, today);
    
    // Controlla se siamo entro il numero di giorni lavorativi configurati
    return workingDaysPassed <= WORKING_DAYS_CONFIG.previousMonthEditingDays;
  }

  // Funzione di utilità per abilitare/disabilitare inserimento worklog
  function isInsertWorklogDisabled(selectedDate) {
    if (!selectedDate) return true;
    const today = new Date();
    const sel = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
    const firstOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const firstOfPreviousMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    
    // Se la data selezionata è antecedente al mese precedente, blocca tutto
    if (sel < firstOfPreviousMonth) {
      return true;
    }
    
    // Se la data selezionata è del mese precedente
    if (sel < firstOfCurrentMonth) {
      // Permetti solo se siamo entro il periodo di editing configurato
      return !isWithinPreviousMonthEditingPeriod();
    }
    
    //if (sel > new Date(today.getFullYear(), today.getMonth(), today.getDate())) return true;
    return false;
  }

  // Funzione per verificare se un worklog esistente è bloccato (stessa logica del bottone + worklog)
  function isExistingWorklogBlocked(worklogDate) {
    if (!worklogDate) return true;
    const today = new Date();
    const sel = new Date(worklogDate.getFullYear(), worklogDate.getMonth(), worklogDate.getDate());
    const firstOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const firstOfPreviousMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    
    // Se la data del worklog è antecedente al mese precedente, blocca tutto
    if (sel < firstOfPreviousMonth) {
      return true;
    }
    
    // Se la data del worklog è del mese precedente
    if (sel < firstOfCurrentMonth) {
      // Permetti solo se siamo entro il periodo di editing configurato
      return !isWithinPreviousMonthEditingPeriod();
    }
    
    return false;
  }

  // Funzione per ottenere il messaggio di restrizione per worklog esistenti
  function getExistingWorklogRestrictionMessage(worklogDate) {
    if (!worklogDate) return 'Invalid date';
    
    const today = new Date();
    const sel = new Date(worklogDate.getFullYear(), worklogDate.getMonth(), worklogDate.getDate());
    const firstOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const firstOfPreviousMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    
    // Se la data del worklog è antecedente al mese precedente
    if (sel < firstOfPreviousMonth) {
      return 'Worklog editing is not allowed for months before the previous month';
    }
    
    // Se la data del worklog è del mese precedente
    if (sel < firstOfCurrentMonth) {
      // Se non siamo entro il periodo di editing configurato
      if (!isWithinPreviousMonthEditingPeriod()) {
        return `Previous month editing is only allowed within the first ${WORKING_DAYS_CONFIG.previousMonthEditingDays} working day(s) of the current month`;
      }
      
      // Se siamo entro il periodo di editing, permette l'editing
      return 'Previous month editing is allowed';
    }
    
    return 'No restrictions';
  }

  // Funzione per verificare se una data è bloccata per un progetto specifico
  function isDateBlockedForProject(date, projectId) {
    if (!date || !projectId) return false;
    
    const project = projects.find(p => p.id === projectId);
    if (!project) return false;
    
    const selectedDate = new Date(date);
    const selectedNormalized = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
    const today = new Date();
    const todayNormalized = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const firstOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const firstOfPreviousMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    
    // Controllo 1: Data successiva alla end_date del progetto
    if (project.end_date) {
      // Fix timezone: parseLocalDate interpreta la data come locale
      const endDate = parseLocalDate(project.end_date);
      const endDateNormalized = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
      if (selectedNormalized > endDateNormalized) return true;
    }
    
    // Controllo 2: Se la data è antecedente al mese precedente, blocca tutto
    if (selectedNormalized < firstOfPreviousMonth) {
      return true;
    }
    
    // Controllo 3: Se la data è del mese precedente
    if (selectedNormalized < firstOfCurrentMonth) {
      // Se non siamo entro il periodo di editing configurato, blocca tutto
      if (!isWithinPreviousMonthEditingPeriod()) return true;
      
      // Se siamo entro il periodo di editing, controlla il campo previous_month_blocked del progetto
      // Se previous_month_blocked è true, blocca l'editing per questo progetto
      if (project.previous_month_blocked === true) return true;
      
      // Se previous_month_blocked è false o null, controlla le restrizioni temporali per il mese precedente
      if (project.days_before_block !== null || project.days_after_block !== null) {
        // Calcola i limiti per il mese precedente (in giorni lavorativi)
        let minAllowedDate = null;
        let maxAllowedDate = null;
        
        // Se days_before_block è null, non c'è limite per le date passate
        if (project.days_before_block !== null) {
          minAllowedDate = addWorkingDays(todayNormalized, -project.days_before_block);
        }
        
        // Se days_after_block è null, non c'è limite per le date future
        if (project.days_after_block !== null) {
          maxAllowedDate = addWorkingDays(todayNormalized, project.days_after_block);
        }
        
        // Verifica se la data è fuori dai limiti per il mese precedente
        if (minAllowedDate && selectedNormalized < minAllowedDate) return true;
        if (maxAllowedDate && selectedNormalized > maxAllowedDate) return true;
      }
      
      // Se arriviamo qui, il mese precedente è permesso
      return false;
    }
    
    // Controllo 4: Restrizioni temporali per il mese corrente (days_before_block e days_after_block)
    // Se non ci sono restrizioni temporali, non è bloccato
    if (project.days_before_block === null && project.days_after_block === null) return false;
    
    // Calcola i limiti (in giorni lavorativi)
    let minAllowedDate = null;
    let maxAllowedDate = null;
    
    // Se days_before_block è null, non c'è limite per le date passate
    if (project.days_before_block !== null) {
      minAllowedDate = addWorkingDays(todayNormalized, -project.days_before_block);
    }
    
    // Se days_after_block è null, non c'è limite per le date future
    if (project.days_after_block !== null) {
      maxAllowedDate = addWorkingDays(todayNormalized, project.days_after_block);
    }
    
    // Verifica se la data è fuori dai limiti
    if (minAllowedDate && selectedNormalized < minAllowedDate) return true;
    if (maxAllowedDate && selectedNormalized > maxAllowedDate) return true;
    
    return false;
  }

  // Funzione per ottenere il messaggio di errore per le restrizioni temporali
  function getDateRestrictionMessage(projectId) {
    const project = projects.find(p => p.id === projectId);
    if (!project) return '';
    
    const today = new Date();
    const selectedNormalized = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
    const firstOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const firstOfPreviousMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    
    // Formato dd-mm-yyyy
    const formatDateDDMMYYYY = (date) => {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };
    
    // Controllo se la data selezionata è antecedente al mese precedente
    if (selectedNormalized < firstOfPreviousMonth) {
      return 'Worklog editing is not allowed for months before the previous month';
    }
    
    // Controllo se la data selezionata è del mese precedente
    if (selectedNormalized < firstOfCurrentMonth) {
      // Se non siamo entro il periodo di editing configurato
      if (!isWithinPreviousMonthEditingPeriod()) {
        return `Previous month creation and editing is only allowed within the first ${WORKING_DAYS_CONFIG.previousMonthEditingDays} working day(s) of the current month`;
      }
      
      // Se siamo entro il periodo di editing, controlla il campo previous_month_blocked del progetto
      if (project.previous_month_blocked === true) {
        return 'Previous month creation and editing is blocked for this project';
      }
      
      // Se previous_month_blocked è false o null, controlla le restrizioni temporali per il mese precedente
      if (project.days_before_block === null && project.days_after_block === null) {
        return 'Previous month creation and editing is allowed for this project';
      } else if (project.days_before_block === null) {
        // Solo limite futuro (in giorni lavorativi)
        const todayNormalized = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const maxAllowedDate = addWorkingDays(todayNormalized, project.days_after_block);
        return `Allowed dates for this project: up to ${formatDateDDMMYYYY(maxAllowedDate)} (${project.days_after_block} working days)`;
      } else if (project.days_after_block === null) {
        // Solo limite passato (in giorni lavorativi)
        const todayNormalized = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const minAllowedDate = addWorkingDays(todayNormalized, -project.days_before_block);
        return `Allowed dates for this project: from ${formatDateDDMMYYYY(minAllowedDate)} onwards (${project.days_before_block} working days)`;
      } else {
        // Entrambi i limiti (in giorni lavorativi)
        const todayNormalized = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const minAllowedDate = addWorkingDays(todayNormalized, -project.days_before_block);
        const maxAllowedDate = addWorkingDays(todayNormalized, project.days_after_block);
        return `Allowed dates for this project: from ${formatDateDDMMYYYY(minAllowedDate)} to ${formatDateDDMMYYYY(maxAllowedDate)} (${project.days_before_block}/${project.days_after_block} working days)`;
      }
    }
    
    // Controllo end_date del progetto
    if (project.end_date) {
      // Fix timezone: parseLocalDate interpreta la data come locale
      const endDate = parseLocalDate(project.end_date);
      const endDateNormalized = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
      
      // Se la data selezionata è dopo la end_date, mostra solo il messaggio di fine progetto
      if (selectedNormalized > endDateNormalized) {
        const endDateFormatted = formatDateDDMMYYYY(endDate);
        return `Project ended on ${endDateFormatted}`;
      }
    }
    
    // Se non c'è end_date o la data selezionata è prima della end_date, usa solo le restrizioni temporali
    if (project.days_before_block === null && project.days_after_block === null) {
      return 'No date restrictions for this project';
    } else if (project.days_before_block === null) {
      // Solo limite futuro (in giorni lavorativi)
      const todayNormalized = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const maxAllowedDate = addWorkingDays(todayNormalized, project.days_after_block);
      return `Allowed dates for this project: up to ${formatDateDDMMYYYY(maxAllowedDate)} (${project.days_after_block} working days)`;
    } else if (project.days_after_block === null) {
      // Solo limite passato (in giorni lavorativi)
      const todayNormalized = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const minAllowedDate = addWorkingDays(todayNormalized, -project.days_before_block);
      return `Allowed dates for this project: from ${formatDateDDMMYYYY(minAllowedDate)} onwards (${project.days_before_block} working days)`;
    } else {
      // Entrambi i limiti (in giorni lavorativi)
      const todayNormalized = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const minAllowedDate = addWorkingDays(todayNormalized, -project.days_before_block);
      const maxAllowedDate = addWorkingDays(todayNormalized, project.days_after_block);
      return `Allowed dates for this project: from ${formatDateDDMMYYYY(minAllowedDate)} to ${formatDateDDMMYYYY(maxAllowedDate)} (${project.days_before_block}/${project.days_after_block} working days)`;
    }
  }

  // Funzione per verificare se l'inserimento è disabilitato considerando le restrizioni del progetto
  function isInsertWorklogDisabledWithProjectRestrictions(selectedDate, projectId) {
    // Prima controlla le restrizioni generali
    if (isInsertWorklogDisabled(selectedDate)) return true;
    
    // Poi controlla le restrizioni specifiche del progetto
    if (projectId && isDateBlockedForProject(selectedDate, projectId)) return true;
    
    return false;
  }

  // Calcola una mappa giorno -> totale ore per il mese visualizzato
  const dayHoursMap = useMemo(() => {
    const map = {};
    rows.forEach(wl => {
      if (wl.date && wl.effort) {
        const dateKey = typeof wl.date === 'string' ? wl.date.slice(0, 10) : wl.date;
        map[dateKey] = (map[dateKey] || 0) + Number(wl.effort);
      }
    });
    return map;
  }, [rows]);

  // Calcola una mappa giorno -> totale ore per il mese visualizzato usando worklogSummary
  const summaryDayHoursMap = useMemo(() => {
    const map = {};
    worklogSummary.forEach(item => {
      if (item.work_date && item.total_effort) {
        const d = new Date(item.work_date);
        const dateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        map[dateKey] = Number(item.total_effort);
      }
    });
    return map;
  }, [worklogSummary]);

  // Modifica CustomDayCentered per usare summaryDayHoursMap
  function CustomDayCentered(day, _value, DayProps) {
    const dateStr = day
      ? `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`
      : '';
    const isCurrentMonth = day && day.getMonth() === selectedDate.getMonth() && day.getFullYear() === selectedDate.getFullYear();
    const totalMinutes = summaryDayHoursMap[dateStr] || 0;
    const totalHours = totalMinutes / 60; // Converti minuti in ore
    
    // Verifica se il giorno è bloccato per il progetto selezionato
    const isBlockedForProject = formData.project && isDateBlockedForProject(day, formData.project);
    
                 let dotColor = null;
             if (totalHours > 8) dotColor = '#f25757'; // rosso se supera le 8h
             else if (totalHours > 0 && totalHours <= 8) dotColor = '#47b1a3'; // blu se tra 0 e 8 ore
             else {
               // Se non ci sono worklog, bollino trasparente
               dotColor = 'transparent'; // trasparente
             }
    const absenceEntry = isCurrentMonth ? isAbsenceDay(dateStr) : null;
    const hasAbsence = !!absenceEntry;
    const tooltipTitle = hasAbsence
      ? `Absence (${absenceEntry.full_day ? 'full day' : 'partial'})${totalHours > 0 ? ` · ${totalHours.toFixed(2)}h` : ''}`
      : `Total hours: ${totalHours.toFixed(2)}`;

    return (
      <MuiTooltip title={tooltipTitle} arrow>
        <Box sx={{ position: 'relative', height: 40 }}>
          <PickersDay
            {...DayProps}
            day={day}
            sx={{
              width: 36,
              height: 36,
              border: 'none !important',
              backgroundColor: 'transparent !important',
              borderRadius: '0 !important',
              boxShadow: 'none !important',
              '&.MuiButtonBase-root-MuiPickersDay-root': {
                border: 'none !important',
                backgroundColor: 'transparent !important',
                borderRadius: '0 !important',
                boxShadow: 'none !important',
              },
              '&.MuiPickersDay-root': {
                border: 'none !important',
                backgroundColor: 'transparent !important',
                borderRadius: '0 !important',
                boxShadow: 'none !important',
              },
              '&:hover': {
                backgroundColor: 'transparent !important',
                border: 'none !important',
              },
              '&:focus': {
                backgroundColor: 'transparent !important',
                border: 'none !important',
                outline: 'none !important',
              },
              ...(DayProps.selected && {
                backgroundColor: '#248b7e !important',
                color: 'white !important',
                border: 'none !important',
                borderRadius: '0 !important',
                '&:hover': {
                  backgroundColor: '#248b7e !important',
                  opacity: 0.9,
                  border: 'none !important',
                },
              }),
              ...(day && new Date().toDateString() === day.toDateString() && !DayProps.selected && {
                border: '1.2px solid #248b7e !important',
                color: '#248b7e !important',
                fontWeight: 700,
                backgroundColor: 'white !important',
                borderRadius: '0 !important',
              }),
              ...(day && day.getDate() === new Date().getDate() &&
                  day.getMonth() === new Date().getMonth() &&
                  day.getFullYear() === new Date().getFullYear() &&
                  !DayProps.selected && {
                border: '1.2px solid #248b7e !important',
                color: '#248b7e !important',
                fontWeight: 700,
                backgroundColor: 'white !important',
                borderRadius: '0 !important',
              })
            }}
          />
          {isCurrentMonth && dotColor && (
            <Box
              sx={{
                position: 'absolute',
                left: hasAbsence ? 4 : 0,
                right: hasAbsence ? 'auto' : 0,
                margin: hasAbsence ? 0 : 'auto',
                bottom: 2,
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: dotColor,
                pointerEvents: 'none',
              }}
            />
          )}
          {hasAbsence && (
            <Box
              sx={{
                position: 'absolute',
                right: 4,
                bottom: 2,
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: absenceEntry.full_day ? '#e53935' : '#f9a825',
                pointerEvents: 'none',
              }}
            />
          )}
        </Box>
      </MuiTooltip>
    );
  }

  // Aggiorna viewedMonth quando cambia mese nel calendario
  const handleMonthChange = (newDate) => {
    if (newDate) {
      console.log('Cambio mese calendario:', newDate);
      setViewedMonth(`${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}`);
      setSelectedDate(new Date(newDate.getFullYear(), newDate.getMonth(), 1));
    }
  };

  // Colonne base
  // Rimuovi la colonna 'Status' e sposta 'Actions' in prima posizione
  const baseColumns = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'project_name', headerName: 'Project', width: 180 },
    { field: 'ticket_id', headerName: 'Ticket ID', width: 180 },
    // { field: 'date', headerName: 'Date', width: 130 }, // nascosta
    { 
      field: 'effort', 
      headerName: 'Effort', 
      width: 100,
      renderCell: (params) => {
        const effortMinutes = Number(params.value);
        const formattedEffort = formatMinutesToHoursMinutes(effortMinutes);
        return formattedEffort;
      }
    },
    { field: 'activity_name', headerName: 'Activity', width: 150 },
    // { field: 'status_name', headerName: 'Status', width: 140 }, // nascosta
    { field: 'effort_type_name', headerName: 'Effort Type', width: 140 },
    {
      field: 'description',
      headerName: 'Description',
      width: 250,
      renderCell: (params) => (
        <Tooltip title={params.value || ''} placement="top" arrow>
          <span style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'block',
            width: '100%'
          }}>{params.value}</span>
        </Tooltip>
      )
    },
  ];
  const actionsColumn = {
    field: 'actions',
    headerName: 'Actions',
    width: 100,
    renderCell: (params) => {
      // Calcola se la data è antecedente al mese corrente
      const today = new Date();
      const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const worklogDate = params.row.date ? new Date(params.row.date) : null;
      
      // Usa la stessa logica del bottone + worklog per determinare se è bloccato
      const isOld = worklogDate && isExistingWorklogBlocked(worklogDate);
      const isBlocked = params.row.status_name === 'Approved' || isOld;
      return (
        <Box>
          {isBlocked ? (
            <Tooltip title={isOld ? getExistingWorklogRestrictionMessage(worklogDate) : "Blocked"}>
              <span>
                <IconButton disabled size="small" style={{ color: '#bdbdbd' }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <Tooltip title="Edit">
              <span>
                <IconButton onClick={() => handleEditDialog(params.row)} size="small">
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {isBlocked ? (
            <Tooltip title={isOld ? getExistingWorklogRestrictionMessage(worklogDate) : "Blocked"}>
              <span>
                <IconButton disabled size="small" style={{ color: '#bdbdbd' }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <Tooltip title="Delete">
              <span>
                <IconButton onClick={() => handleDeleteWorklog(params.row.id)} size="small">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      );
    }
  };
  const columns = [actionsColumn, ...baseColumns];

  // Prima di passare rows al DataGrid, ordina per created_at crescente (o id crescente se non c'è created_at)
  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      if (a.created_at && b.created_at) {
        return new Date(a.created_at) - new Date(b.created_at);
      }
      if (a.id && b.id) {
        return a.id - b.id;
      }
      return 0;
    });
  }, [rows]);

  // Gestione apertura dialog
  const handleOpenDialog = () => {
    setFormData({
      user: selectedUser, // Prevalorizza con l'utente selezionato
      project: '',
      activity: '',
      status: '',
      effort_type: '',
      effort: '',
      ticket_id: '',
      description: '',
    });
    setEditingRow(null);
    setFormError(null);
    setOpenDialog(true);
  };
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormError(null);
  };

  // Funzione per gestire la prevalorizzazione del ticket_id
  const handleTicketIdPrefix = (projectId, activityId) => {
    if (!projectId || !activityId) return '';
    
    // Trova il progetto selezionato
    const selectedProject = projects.find(p => p.id === projectId);
    if (!selectedProject || !selectedProject.ticket_id_prefix) return '';
    
    // Trova l'attività selezionata
    const selectedActivity = activities.find(a => a.id === activityId);
    if (!selectedActivity || !selectedActivity.requires_ticket_id) return '';
    
    // Controlla se l'attività è "ticket resolution" (AM o ENH)
    const activityName = selectedActivity.name.toLowerCase();
    const isTicketResolution = activityName.includes('ticket resolution') || 
                              activityName.includes('am') || 
                              activityName.includes('enh');
    
    if (isTicketResolution) {
      return selectedProject.ticket_id_prefix;
    }
    
    return '';
  };

  // Gestione form
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Gestione apertura dialog per update
  const handleEditDialog = async (row) => {
    // Mantieni effort in minuti (solo numero, senza 'm')
    const effortMinutes = row.effort ? String(Number(row.effort)) : '';
    setFormData({
      id: row.id || '',
      user: row.user_id || '',
      project: row.project_id || '',
      activity: row.activity_id || '',
      status: row.status_id || '',
      effort_type: row.effort_type_id || '',
      effort: effortMinutes,
      ticket_id: (row.ticket_id || '').trim(),
      description: row.description || '',
    });
    setEditingRow(row);
    setFormError(null);

    // Carica le attività permesse per il progetto del worklog in modifica
    if (row.project_id) {
      const ids = await fetchAllowedActivityIdsForProject(row.project_id);
      setAllowedActivitiesByProject(prev => ({ ...prev, single: ids }));
    } else {
      setAllowedActivitiesByProject(prev => ({ ...prev, single: [] }));
    }

    setOpenDialog(true);
  };

  // Salva nuovo o update worklog
  const handleSaveWorklog = async () => {
    setSaving(true);
    setFormError(null);
    
    // Trova l'attività selezionata per verificare se richiede ticket_id/description
    const selectedActivity = activities.find(a => a.id === formData.activity);
    const requiresTicketId = selectedActivity?.requires_ticket_id;
    const requiresDescription = selectedActivity?.requires_description;
    
    // Validazione campi obbligatori
    const requiredFields = ['user', 'project', 'activity', 'effort_type', 'effort'];
    if (requiresTicketId) {
      requiredFields.push('ticket_id');
    }
    if (requiresDescription) {
      requiredFields.push('description');
    }
    
    const missingFields = requiredFields.filter(field => !formData[field]);
    if (missingFields.length > 0) {
      const fieldNames = missingFields.map(field => field.replace('_', ' ').toUpperCase());
      setFormError(`Please fill in all required fields: ${fieldNames.join(', ')}`);
      setSaving(false);
      return;
    }
    
    // Converti effort da formato ore:minuti a minuti
    const effortResult = parseEffortToMinutes(formData.effort);
    if (!effortResult.isValid) {
      setFormError(effortResult.error);
      setSaving(false);
      return;
    }
    
    const effortMinutes = effortResult.minutes;

    // Validazione regex su ticket_id se prevista a livello progetto
    const selectedProjectForRegex = projects.find(p => p.id === formData.project);
    if (requiresTicketId && selectedProjectForRegex?.regex) {
      try {
        let regexStr = String(selectedProjectForRegex.regex || '').trim();
        let flags = '';
        // Supporta formato /pattern/flags eventualmente salvato a db
        if (regexStr.startsWith('/') && regexStr.lastIndexOf('/') > 0) {
          const lastSlash = regexStr.lastIndexOf('/');
          flags = regexStr.slice(lastSlash + 1);
          regexStr = regexStr.slice(1, lastSlash);
        }
        const pattern = new RegExp(regexStr, flags);
        const prefix = (selectedProjectForRegex.ticket_id_prefix || '').trim();
        const raw = (formData.ticket_id || '').trim();
        const toValidate = prefix && raw.startsWith(prefix) ? raw.slice(prefix.length).trim() : raw;
        console.debug('[TICKET_ID VALIDATION] single', { regexStr, flags, prefix, raw, toValidate, requiresTicketId });
        const match = pattern.test(toValidate);
        console.debug('[TICKET_ID VALIDATION] single result', { match });
        if (!match) {
          setFormError('Ticket ID format not valid for this project');
          setSaving(false);
          return;
        }
      } catch (e) {
        // In caso di regex non valida a db, evita blocco inserimento ma logga lato console
        console.warn('Invalid project regex:', selectedProjectForRegex.regex, e);
      }
    }
    if (effortMinutes === 0 && formData.effort.trim() !== '') {
      setFormError('Please enter effort in valid format (e.g., 30, 60, 120)');
      setSaving(false);
      return;
    }
    
    // Calcola il totale dell'effort per il giorno (escludendo il worklog corrente se in modifica)
    const currentWorklogEffort = editingRow ? Number(editingRow.effort || 0) : 0;
    const newWorklogEffort = effortMinutes;
    const otherWorklogsEffort = rows
      .filter(row => !editingRow || row.id !== editingRow.id)
      .reduce((sum, row) => sum + Number(row.effort || 0), 0);
    
    const totalDailyEffort = otherWorklogsEffort + newWorklogEffort;
    
    // Validazione: totale giornaliero deve essere almeno 1 minuto
    if (totalDailyEffort < 1) {
      setFormError('Total daily effort must be at least 1 minute');
      setSaving(false);
      return;
    }
    
    // Validazione: totale giornaliero non può superare 24 ore (1440 minuti)
    if (totalDailyEffort > 1440) {
      setFormError('Total daily effort cannot exceed 24 hours');
      setSaving(false);
      return;
    }
    
    // Validazione lunghezza descrizione
    if (formData.description && formData.description.length > 400) {
      setFormError('Description cannot exceed 400 characters');
      setSaving(false);
      return;
    }
    // Se l'attività richiede descrizione, assicurati che non sia vuota o solo spazi
    if (requiresDescription && (!formData.description || String(formData.description).trim() === '')) {
      setFormError('Description is required for this activity');
      setSaving(false);
      return;
    }
    
    // Controllo warning per effort superiore a 8 ore (480 minuti)
    if (totalDailyEffort > 480) {
      // Mostra warning sia per nuovi worklog che per modifiche
      setPendingWorklogData({
        payload: {
          id: editingRow ? editingRow.id : '',
          user_id: formData.user,
          project_id: formData.project,
          activity_id: formData.activity,
          status_id: formData.status || '1',
          effort_type_id: formData.effort_type,
          effort: effortMinutes,
          ticket_id: cleanTicketId(formData.ticket_id, selectedProjectForRegex?.ticket_id_prefix),
          description: formData.description,
          date: formatDate(selectedDate),
        },
        isEditing: !!editingRow
      });
      setShowWarningDialog(true);
      setSaving(false);
      return;
    }
    
    try {
      const payload = {
        id: editingRow ? editingRow.id : '',
        user_id: formData.user,
        project_id: formData.project,
        activity_id: formData.activity,
        status_id: formData.status || '1',
        effort_type_id: formData.effort_type,
        effort: effortMinutes, // Usa i minuti convertiti
        ticket_id: cleanTicketId(formData.ticket_id, selectedProjectForRegex?.ticket_id_prefix),
        description: formData.description,
        date: formatDate(selectedDate),
      };
      let response;
      if (editingRow) {
        // response = await fetch(`${API_ENDPOINTS.WORKLOGS}?id=${editingRow.id}`, {
          response = await fetch(`${API_ENDPOINTS.WORKLOGS}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        response = await fetch(API_ENDPOINTS.WORKLOGS, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }
      if (!response.ok) {
        const err = await response.text();
        throw new Error(err);
      }
      setOpenDialog(false);
      setEditingRow(null);
      // Ricarica worklog
      const dateStr = formatDate(selectedDate);
      const reload = await fetch(`${API_ENDPOINTS.WORKLOGS}?date=${dateStr}&user_id=${selectedUser}`);
      let data = await reload.json();
      const arr = data.worklogs || [];
      // Filtro per la data selezionata (solo yyyy-mm-dd)
      const filtered = arr.filter(wl => wl.date && wl.date.startsWith(dateStr) && String(wl.user_id || wl.user?.id) === String(selectedUser));
      setRows(filtered.map((row, idx) => ({ id: row.id || idx, ...row })));
      await refreshWorklogSummary();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Gestisce la conferma del warning per effort superiore a 8 ore
  const handleWarningConfirm = async () => {
    if (!pendingWorklogData) return;
    
    setSaving(true);
    setShowWarningDialog(false);
    
    try {
      const { payload, isEditing, isMulti } = pendingWorklogData;
      
      if (isMulti) {
        // Gestione multi worklog
        const { multiRows, multiCommon } = payload;
        const dateStr = formatDate(selectedDate);
        const multiProject = projects.find(p => p.id === multiCommon.project);
        
        for (const row of multiRows) {
          const effortResult = parseEffortToMinutes(row.effort);
          const effortMinutes = effortResult.minutes;
          const payload = {
            user_id: multiCommon.user,
            project_id: multiCommon.project,
            activity_id: multiCommon.activity,
            status_id: '1',
            effort_type_id: multiCommon.effort_type,
            effort: effortMinutes,
            ticket_id: cleanTicketId(row.ticket_id, multiProject?.ticket_id_prefix),
            description: row.description,
            date: dateStr,
          };
          const response = await fetch(API_ENDPOINTS.WORKLOGS, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
          
          if (!response.ok) {
            const err = await response.text();
            throw new Error(err);
          }
        }
        
        setOpenMultiDialog(false);
        setPendingWorklogData(null);
        
        // Ricarica worklog
        const reload = await fetch(`${API_ENDPOINTS.WORKLOGS}?date=${dateStr}&user_id=${selectedUser}`);
        let data = await reload.json();
        const arr = data.worklogs || [];
        const filtered = arr.filter(wl => wl.date && wl.date.startsWith(dateStr) && String(wl.user_id || wl.user?.id) === String(selectedUser));
        setRows(filtered.map((row, idx) => ({ id: row.id || idx, ...row })));
        await refreshWorklogSummary();
      } else {
        // Gestione singolo worklog
        let response;
        
        if (isEditing) {
          response = await fetch(`${API_ENDPOINTS.WORKLOGS}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
        } else {
          response = await fetch(API_ENDPOINTS.WORKLOGS, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
        }
        
        if (!response.ok) {
          const err = await response.text();
          throw new Error(err);
        }
        
        setOpenDialog(false);
        setEditingRow(null);
        setPendingWorklogData(null);
        
        // Ricarica worklog
        const dateStr = formatDate(selectedDate);
        const reload = await fetch(`${API_ENDPOINTS.WORKLOGS}?date=${dateStr}&user_id=${selectedUser}`);
        let data = await reload.json();
        const arr = data.worklogs || [];
        const filtered = arr.filter(wl => wl.date && wl.date.startsWith(dateStr) && String(wl.user_id || wl.user?.id) === String(selectedUser));
        setRows(filtered.map((row, idx) => ({ id: row.id || idx, ...row })));
        await refreshWorklogSummary();
      }
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleWarningCancel = () => {
    setShowWarningDialog(false);
    setPendingWorklogData(null);
    setSaving(false);
    setMultiSaving(false);
  };

  // Elimina worklog
  const handleDeleteWorklog = async (id) => {
    // Trova il worklog da eliminare per controllare le restrizioni
    const worklogToDelete = rows.find(row => row.id === id);
    if (worklogToDelete) {
      // Fix timezone: parseLocalDate interpreta la data come locale
      const worklogDate = worklogToDelete.date ? parseLocalDate(worklogToDelete.date) : null;
      
      // Controlla se l'eliminazione è bloccata (stessa logica del bottone + worklog)
      if (worklogDate && isExistingWorklogBlocked(worklogDate)) {
        const restrictionMessage = getExistingWorklogRestrictionMessage(worklogDate);
        alert(`Cannot delete worklog: ${restrictionMessage}`);
        return;
      }
    }
    
    if (!window.confirm('Are you sure you want to delete this worklog?')) return;
    setLoading(true);
    setError(null);
    try {
      //const response = await fetch(`${API_ENDPOINTS.WORKLOGS}/${id}`, { method: 'DELETE' });
      const response = await fetch(`${API_ENDPOINTS.WORKLOGS}?id=${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete worklog');
      // Ricarica worklog
      const dateStr = formatDate(selectedDate);
      const reload = await fetch(`${API_ENDPOINTS.WORKLOGS}?date=${dateStr}&user_id=${selectedUser}`);
      let data = await reload.json();
      const arr = data.worklogs || [];
      // Filtro per la data selezionata (solo yyyy-mm-dd)
      const filtered = arr.filter(wl => wl.date && wl.date.startsWith(dateStr) && String(wl.user_id || wl.user?.id) === String(selectedUser));
      setRows(filtered.map((row, idx) => ({ id: row.id || idx, ...row })));
      await refreshWorklogSummary();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Multi Worklogs
  const handleOpenMultiDialog = () => {
    checkAbsenceWarningForWorklog();
    setMultiCommon({ user: selectedUser, project: '', activity: '', effort_type: '', status: '' });
    setMultiRows([{ ticket_id: '', effort: '', status: '', description: '' }]);
    setMultiError(null);
    setAllowedActivitiesByProject(prev => ({ ...prev, multi: [] }));
    fetch(API_ENDPOINTS.PROJECTS)
      .then(res => res.json())
      .then(async data => {
        let arr = data.projects || [];
        const allProjects = Array.isArray(arr) ? arr : [];
        
        // Filtra i progetti per l'utente corrente
        console.log('🔍 backendUser available:', !!backendUser, backendUser);
        if (backendUser) {
          const filteredProjects = await filterProjectsForUser(backendUser, selectedDate);
          setProjects(filteredProjects);
          

        } else {
          console.log('❌ No backendUser available, showing all projects');
          setProjects(allProjects);
        }
      });
    fetch(API_ENDPOINTS.ACTIVITIES)
      .then(res => res.json())
      .then(data => {
        let arr = data.activities || [];
        setActivities(Array.isArray(arr) ? arr.filter(a => String(a.name || '').toUpperCase() !== 'FORECAST') : []);
      });
    fetch(API_ENDPOINTS.STATUS)
      .then(res => res.json())
      .then(data => {
        let statusArr = data.statuses || [];
        setStatuses(Array.isArray(statusArr) ? statusArr : []);
      });
    fetch(API_ENDPOINTS.USERS)
      .then(res => res.json())
      .then(data => {
        let usersArr = data.users || [];
        setUsers(Array.isArray(usersArr) ? usersArr : []);
      });
    fetch(API_ENDPOINTS.EFFORT_TYPES)
      .then(res => res.json())
      .then(data => {
        let effortTypesArr = data.effort_types || [];
        setEffortTypes(Array.isArray(effortTypesArr) ? effortTypesArr : []);
      });
    setOpenMultiDialog(true);
  };
  const handleCloseMultiDialog = () => {
    setOpenMultiDialog(false);
    setMultiError(null);
  };
  // Funzione per gestire la prevalorizzazione del ticket_id nel dialog multiplo
  const handleMultiTicketIdPrefix = (projectId, activityId) => {
    if (!projectId || !activityId) return '';
    
    // Trova il progetto selezionato
    const selectedProject = projects.find(p => p.id === projectId);
    if (!selectedProject || !selectedProject.ticket_id_prefix) return '';
    
    // Trova l'attività selezionata
    const selectedActivity = activities.find(a => a.id === activityId);
    if (!selectedActivity || !selectedActivity.requires_ticket_id) return '';
    
    // Controlla se l'attività è "ticket resolution" (AM o ENH)
    const activityName = selectedActivity.name.toLowerCase();
    const isTicketResolution = activityName.includes('ticket resolution') || 
                              activityName.includes('am') || 
                              activityName.includes('enh');
    
    if (isTicketResolution) {
      return selectedProject.ticket_id_prefix;
    }
    
    return '';
  };

  const handleMultiCommonChange = (e) => {
    const { name, value } = e.target;
    setMultiCommon({ ...multiCommon, [name]: value });
  };
  const handleMultiRowChange = (idx, e) => {
    const { name, value } = e.target;
    const updated = [...multiRows];
    updated[idx][name] = value;
    setMultiRows(updated);
  };
  const handleAddMultiRow = () => {
    // Calcola il prefix per la nuova riga
    const ticketIdPrefix = handleMultiTicketIdPrefix(multiCommon.project, multiCommon.activity);
    setMultiRows([...multiRows, { ticket_id: ticketIdPrefix || '', effort: '', status: '', description: '' }]);
  };
  const handleRemoveMultiRow = (idx) => {
    setMultiRows(multiRows.filter((_, i) => i !== idx));
  };
  const handleSaveMultiWorklogs = async () => {
    setMultiSaving(true);
    setMultiError(null);
    // Validazione
    if (!multiCommon.project || !multiCommon.activity || !multiCommon.effort_type) {
      setMultiError('Please fill in all common fields.');
      setMultiSaving(false);
      return;
    }
    
    // Trova l'attività selezionata per verificare se richiede ticket_id/description
    const selectedActivity = activities.find(a => a.id === multiCommon.activity);
    const requiresTicketId = selectedActivity?.requires_ticket_id;
    const requiresDescription = selectedActivity?.requires_description;
    
    // Calcola il totale dell'effort per tutti i worklog da inserire
    let totalNewEffort = 0;
    for (const row of multiRows) {
      // Validazione campi obbligatori per ogni riga
      const requiredFields = ['effort'];
      if (requiresTicketId) {
        requiredFields.push('ticket_id');
      }
      if (requiresDescription) {
        requiredFields.push('description');
      }
      
      const missingFields = requiredFields.filter(field => !row[field]);
      if (missingFields.length > 0) {
        const fieldNames = missingFields.map(field => field.replace('_', ' ').toUpperCase());
        setMultiError(`Please fill in all required fields: ${fieldNames.join(', ')}`);
        setMultiSaving(false);
        return;
      }
      
      // Converti effort da formato ore:minuti a minuti
      const effortResult = parseEffortToMinutes(row.effort);
      if (!effortResult.isValid) {
        setMultiError(effortResult.error);
        setMultiSaving(false);
        return;
      }
      
      const effortMinutes = effortResult.minutes;

      // Validazione regex su ticket_id per ogni riga se prevista e richiesta
      const selectedProjectForRegex = projects.find(p => p.id === multiCommon.project);
      if (requiresTicketId && selectedProjectForRegex?.regex) {
        try {
          let regexStr = String(selectedProjectForRegex.regex || '').trim();
          let flags = '';
          if (regexStr.startsWith('/') && regexStr.lastIndexOf('/') > 0) {
            const lastSlash = regexStr.lastIndexOf('/');
            flags = regexStr.slice(lastSlash + 1);
            regexStr = regexStr.slice(1, lastSlash);
          }
          const pattern = new RegExp(regexStr, flags);
          const prefix = (selectedProjectForRegex.ticket_id_prefix || '').trim();
          const raw = (row.ticket_id || '').trim();
          const toValidate = prefix && raw.startsWith(prefix) ? raw.slice(prefix.length).trim() : raw;
          console.debug('[TICKET_ID VALIDATION] multi', { regexStr, flags, prefix, raw, toValidate, requiresTicketId });
          const match = pattern.test(toValidate);
          console.debug('[TICKET_ID VALIDATION] multi result', { match });
          if (!match) {
            setMultiError('Ticket ID format not valid for this project');
            setMultiSaving(false);
            return;
          }
        } catch (e) {
          console.warn('Invalid project regex:', selectedProjectForRegex.regex, e);
        }
      }
      if (effortMinutes === 0 && row.effort.trim() !== '') {
        setMultiError('Please enter effort in valid format (e.g., 30, 60, 120)');
        setMultiSaving(false);
        return;
      }
      
      totalNewEffort += effortMinutes;
      
      // Validazione lunghezza descrizione
      if (row.description && row.description.length > 400) {
        setMultiError('Description cannot exceed 400 characters');
        setMultiSaving(false);
        return;
      }
      if (requiresDescription && (!row.description || String(row.description).trim() === '')) {
        setMultiError('Description is required for this activity');
        setMultiSaving(false);
        return;
      }
    }
    
    // Calcola il totale dell'effort esistente per il giorno
    const existingWorklogsEffort = rows.reduce((sum, row) => sum + Number(row.effort || 0), 0);
    const totalDailyEffort = existingWorklogsEffort + totalNewEffort;
    
    // Validazione: totale giornaliero deve essere almeno 1 minuto
    if (totalDailyEffort < 1) {
      setMultiError('Total daily effort must be at least 1 minute');
      setMultiSaving(false);
      return;
    }
    
    // Validazione: totale giornaliero non può superare 24 ore (1440 minuti)
    if (totalDailyEffort > 1440) {
      setMultiError('Total daily effort cannot exceed 24 hours');
      setMultiSaving(false);
      return;
    }
    
    // Controllo warning per effort superiore a 8 ore (480 minuti)
    if (totalDailyEffort > 480) {
      // Mostra warning per multi worklog che superano 8 ore
      setPendingWorklogData({
        payload: {
          multiRows: multiRows,
          multiCommon: multiCommon,
          totalDailyEffort: totalDailyEffort
        },
        isMulti: true
      });
      setShowWarningDialog(true);
      setMultiSaving(false);
      return;
    }
    try {
      const dateStr = formatDate(selectedDate);
      const multiProject = projects.find(p => p.id === multiCommon.project);
      for (const row of multiRows) {
        const effortResult = parseEffortToMinutes(row.effort);
        const effortMinutes = effortResult.minutes;
        const payload = {
          user_id: multiCommon.user,
          project_id: multiCommon.project,
          activity_id: multiCommon.activity,
          status_id: '1', // invia sempre stringa vuota per evitare errori
          effort_type_id: multiCommon.effort_type,
          effort: effortMinutes, // Usa i minuti convertiti
          ticket_id: cleanTicketId(row.ticket_id, multiProject?.ticket_id_prefix),
          description: row.description,
          date: dateStr,
        };
        await fetch(API_ENDPOINTS.WORKLOGS, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }
      setOpenMultiDialog(false);
      // Ricarica worklog
      const reload = await fetch(`${API_ENDPOINTS.WORKLOGS}?date=${dateStr}&user_id=${selectedUser}`);
      let data = await reload.json();
      const arr = data.worklogs || [];
      // Filtro per la data selezionata (solo yyyy-mm-dd)
      const filtered = arr.filter(wl => wl.date && wl.date.startsWith(dateStr) && String(wl.user_id || wl.user?.id) === String(selectedUser));
      setRows(filtered.map((row, idx) => ({ id: row.id || idx, ...row })));
      await refreshWorklogSummary();
    } catch (err) {
      setMultiError('Error saving worklogs.');
    } finally {
      setMultiSaving(false);
    }
  };

  // Funzione per aggiornare il summary dei worklog (bollini calendario)
  const refreshWorklogSummary = async () => {
    if (!selectedUser || !viewedMonth) return;
    try {
      const res = await fetch(`${API_ENDPOINTS.USER_EFFORT_SUMMARY}?user_id=${selectedUser}&year_month=${viewedMonth}`);
      const data = await res.json();
      setWorklogSummary(Array.isArray(data.data) ? data.data : []);
    } catch {
      setWorklogSummary([]);
    }
  };

  const refreshAbsenceDays = async () => {
    if (!selectedUser || !viewedMonth || selectedUser === 'ALL') return;
    getAbsences(selectedUser, viewedMonth).then(setAbsenceDays).catch(() => setAbsenceDays([]));
  };

  function isAbsenceDay(dateStr) {
    return absenceDays.find(v => v.date === dateStr) || null;
  }

  const handleOpenAbsenceDialog = () => {
    setAbsDialogNavDate(selectedDate || new Date());
    setSelectedAbsenceDates(new Set());
    setAbsenceDatesToRemove(new Set());
    setAbsenceFullDay(true);
    setOpenAbsenceDialog(true);
  };

  const handleSaveAbsences = async () => {
    if (selectedAbsenceDates.size === 0 && absenceDatesToRemove.size === 0) { setOpenAbsenceDialog(false); return; }
    setAbsenceSaving(true);
    try {
      if (selectedAbsenceDates.size > 0) {
        const items = Array.from(selectedAbsenceDates).map(d => ({ date: d, full_day: absenceFullDay }));
        await addAbsences(selectedUser, items);
      }
      for (const dateStr of absenceDatesToRemove) {
        const entry = dialogAbsenceDays.find(v => v.date === dateStr);
        if (entry) await deleteAbsence(entry.id);
      }
      await refreshAbsenceDays();
      setOpenAbsenceDialog(false);
    } finally {
      setAbsenceSaving(false);
    }
  };

  const handleDeleteAbsence = async (id) => {
    await deleteAbsence(id);
    await refreshAbsenceDays();
  };

  const checkAbsenceWarningForWorklog = () => {
    const dateStr = formatDate(selectedDate);
    const entry = isAbsenceDay(dateStr);
    if (entry && entry.full_day) {
      setAbsenceWarningOpen(true);
    }
  };

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
      {/* Main table centrale */}
      <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
        <Box my={4}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' } }}>
            <Typography variant="h4" gutterBottom sx={{ fontSize: '1.5rem', textAlign: { xs: 'left', md: 'center' } }}>
              Daily Tracking for{' '}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {selectedDate ? `${selectedDate.toLocaleDateString('en-US', { weekday: 'long' })}, ${String(selectedDate.getDate()).padStart(2, '0')}.${String(selectedDate.getMonth() + 1).padStart(2, '0')}.${selectedDate.getFullYear()}` : ''}
              </Box>
            </Typography>
            <Box sx={{ width: { xs: '100%', md: 'auto' }, mt: { xs: 2, md: 0 }, display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <Tooltip title={isInsertWorklogDisabled(selectedDate) ? 'Worklog insertion is disabled for previous months and future days' : 'Add Worklog'}>
                <span>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    color="primary"
                    onClick={handleOpenMultiDialog}
                    disabled={isInsertWorklogDisabled(selectedDate)}
                  >
                    Worklog
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Add Absence Days">
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<EventBusyIcon />}
                    color="warning"
                    onClick={handleOpenAbsenceDialog}
                    disabled={selectedUser === 'ALL'}
                  >
                    Absence
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>
          {(() => {
            const dateStr = formatDate(selectedDate);
            const absEntry = absenceDays.find(v => v.date === dateStr);
            if (!absEntry) return null;
            return (
              <Box sx={{ mb: 1.5 }}>
                <Chip
                  icon={<EventBusyIcon />}
                  label={absEntry.full_day ? "Absence - Full Day" : "Absence - Partial"}
                  color={absEntry.full_day ? "error" : "warning"}
                  variant="filled"
                  onDelete={() => handleDeleteAbsence(absEntry.id)}
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            );
          })()}
          {loading ? (
            <CircularProgress />
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <Box sx={{ height: { xs: 'auto', md: 'calc(100vh - 100px)' }, width: '100%' }}>
              <DataGrid
                rows={sortedRows}
                columns={columns}
                pagination
                initialState={{
                  pagination: { paginationModel: { pageSize: 10 } },
                  columns: {
                    columnVisibilityModel: { id: false },
                  },
                }}
                pageSizeOptions={[10]}
                autoHeight
                sx={{ bgcolor: 'white' }}
                sortModel={[{ field: 'id', sort: 'asc' }]}
              />
            </Box>
          )}
        </Box>
      </Box>
      {/* Sidebar destra: calendario, pie chart e riepilogo - ora sotto la tabella su mobile */}
      <Box sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0, mt: { xs: 1, md: 0 }, pt: 0 }}>
        {/* Fix per padding/margin eccessivo del calendario su mobile */}
        <GlobalStyles styles={{
          '@media (max-width: 900px)': {
            '.css-xmy7el': {
              paddingTop: 0,
              marginTop: 0,
            }
          }
        }} />
        <GlobalStyles styles={{
        '.css-v0ohlj': {
          width: 'auto !important'
        },
        '.PrivatePickersSlideTransition-root.MuiDayPicker-slideTransition': {
          minHeight: '260px !important',
        },
      }} />
        {/* Calendario */}
        <Box sx={{
          bgcolor: 'white',
          borderRadius: 2, // uguale ai box sottostanti
          border: '1px solid #e0e0e0',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          width: '100%',
          mb: 2,
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'visible',
          flexShrink: 0,
          flexGrow: 0,
          minHeight: '304px',
        }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <StaticDatePicker
              displayStaticWrapperAs="desktop"
              value={selectedDate}
              onChange={setSelectedDate}
              showDaysOutsideCurrentMonth
              onMonthChange={handleMonthChange}
              renderInput={(params) => <TextField {...params} fullWidth />}
              renderDay={(day, _value, DayProps) => CustomDayCentered(day, _value, DayProps)}
              slotProps={{
                layout: { sx: { width: '100%' } },
                textField: { sx: { width: '100%' } }
              }}
              sx={{ 
                width: '100%', 
                borderRadius: 0, 
                bgcolor: 'transparent', 
                boxShadow: 'none', 
                border: 'none',
                '& .MuiPickersDay-root': {
                  border: 'none !important',
                  backgroundColor: 'transparent !important',
                  borderRadius: '0 !important',
                  boxShadow: 'none !important',
                },
                '& .MuiPickersDay-root:hover': {
                  backgroundColor: 'transparent !important',
                  border: 'none !important',
                },
                '& .MuiPickersDay-root:focus': {
                  backgroundColor: 'transparent !important',
                  border: 'none !important',
                  outline: 'none !important',
                }
              }}
            />
          </LocalizationProvider>
        </Box>
        {/* Pie Chart - Total hours by Project */}
        <Box sx={{ bgcolor: 'white', borderRadius: 2, boxShadow: 1, p: 2, mb: { xs: 1, md: 2 }, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', boxSizing: 'border-box', maxWidth: '100%' }}>
          <Typography variant="subtitle1" align="center" sx={{ mb: 1 }}>Total hours by Project</Typography>
          {Object.keys(projectTotals).length === 0 ? (
            <Typography color="text.secondary" align="center">No data</Typography>
          ) : (
            <Box component="ul" sx={{ width: '100%', listStyle: 'none', p: 0, m: 0 }}>
              {Object.entries(projectTotals).map(([project, value]) => {
                const hours = value / 60; // Converti minuti in ore
                return (
                  <Box component="li" key={project} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #f0f0f0', width: '100%' }}>
                    <Typography variant="body2">{project}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{hours.toFixed(2)} h</Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
        {/* Widget totale ore tracciate */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: 'white', borderRadius: 2, boxShadow: 1, p: 2, mb: { xs: 1, md: 2 } }}>
          <Badge
            variant="dot"
            sx={{
              mr: 2,
              '& .MuiBadge-dot': {
                backgroundColor:
                  totalHours === 0 ? 'transparent' :
                  totalHours > 0 && totalHours <= 8 ? '#47b1a3' :
                  totalHours > 8 ? '#f25757' :
                  'transparent',
              },
            }}
          >
            <Typography variant="h5">{totalHours.toFixed(2)}</Typography>
          </Badge>
          <Typography variant="subtitle1">Total hours tracked</Typography>
          {totalHours > 8 && (
            <Alert severity="error" sx={{ mt: 1, width: '100%', alignItems: 'center', justifyContent: 'center'}}>
              Daily effort exceeds 8 hours
            </Alert>
          )}
        </Box>
      </Box>
      {/* Dialog per aggiunta worklog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>{editingRow ? `Update Worklog for ${formatDate(selectedDate)}` : `New Worklog for ${formatDate(selectedDate)}`}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete
              options={projects}
              getOptionLabel={(option) => {
                return `${option.name}${option.customer?.name ? ` - ${option.customer.name}` : ''}`;
              }}
              value={projects.find(p => p.id === formData.project) || null}
              onChange={(event, newValue) => {
                const newProjectId = newValue ? newValue.id : '';
                const ticketIdPrefix = handleTicketIdPrefix(newProjectId, formData.activity);
                setFormData(prev => ({
                  ...prev,
                  project: newProjectId,
                  ticket_id: ticketIdPrefix || prev.ticket_id
                }));
                // Carica activity consentite per il progetto (single dialog)
                (async () => {
                  const ids = await fetchAllowedActivityIdsForProject(newProjectId);
                  setAllowedActivitiesByProject(prev => ({ ...prev, single: ids }));
                  // Se l'attività selezionata non è più valida, resettala
                  if (ids.length && formData.activity && !ids.includes(formData.activity)) {
                    setFormData(prev => ({ ...prev, activity: '' }));
                  }
                })();
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Project"
                  required
                  name="project"
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'text.primary'
                      }}
                    >
                      {option.name}
                    </Typography>
                    {option.customer?.name && (
                      <Typography variant="caption" color="textSecondary">
                        {option.customer.name}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
            <Autocomplete
              options={activities}
              getOptionLabel={(option) => option.name || ''}
              value={activities.find(a => a.id === formData.activity) || null}
              onChange={(event, newValue) => {
                const newActivityId = newValue ? newValue.id : '';
                const ticketIdPrefix = handleTicketIdPrefix(formData.project, newActivityId);
                setFormData(prev => ({
                  ...prev,
                  activity: newActivityId,
                  ticket_id: ticketIdPrefix || prev.ticket_id
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Activity"
                  required
                  name="activity"
                />
              )}
              filterOptions={(opts) => {
                const allowed = allowedActivitiesByProject.single;
                if (!formData.project || !Array.isArray(allowed) || allowed.length === 0) return opts;
                return opts.filter(o => allowed.includes(o.id));
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
            <Autocomplete
              options={effortTypes}
              getOptionLabel={(option) => option.name || ''}
              value={effortTypes.find(et => et.id === formData.effort_type) || null}
              onChange={(event, newValue) => {
                setFormData(prev => ({
                  ...prev,
                  effort_type: newValue ? newValue.id : ''
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Effort Type"
                  required
                  name="effort_type"
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
            <TextField
              name="ticket_id"
              label="Ticket ID"
              value={formData.ticket_id}
              onChange={(e) => {
                const selectedProject = projects.find(p => p.id === formData.project);
                const selectedActivity = activities.find(a => a.id === formData.activity);
                
                if (selectedProject?.ticket_id_prefix && selectedActivity?.requires_ticket_id) {
                  const activityName = selectedActivity.name.toLowerCase();
                  const isTicketResolution = activityName.includes('ticket resolution') || 
                                            activityName.includes('am') || 
                                            activityName.includes('enh');
                  
                  if (isTicketResolution) {
                    const prefix = selectedProject.ticket_id_prefix;
                    const newValue = e.target.value;
                    
                    // Se il nuovo valore non inizia con il prefix, mantieni il prefix
                    if (!newValue.startsWith(prefix)) {
                      e.target.value = prefix;
                    }
                  }
                }
                
                handleFormChange(e);
              }}
              onKeyDown={(e) => {
                const selectedProject = projects.find(p => p.id === formData.project);
                const selectedActivity = activities.find(a => a.id === formData.activity);
                
                if (selectedProject?.ticket_id_prefix && selectedActivity?.requires_ticket_id) {
                  const activityName = selectedActivity.name.toLowerCase();
                  const isTicketResolution = activityName.includes('ticket resolution') || 
                                            activityName.includes('am') || 
                                            activityName.includes('enh');
                  
                  if (isTicketResolution) {
                    const prefix = selectedProject.ticket_id_prefix;
                    const currentValue = e.target.value;
                    const cursorPosition = e.target.selectionStart;
                    
                    // Se l'utente prova a cancellare il prefix (Backspace o Delete)
                    if ((e.key === 'Backspace' || e.key === 'Delete') && cursorPosition < prefix.length) {
                      e.preventDefault();
                    }
                  }
                }
              }}
              onSelect={(e) => {
                const selectedProject = projects.find(p => p.id === formData.project);
                const selectedActivity = activities.find(a => a.id === formData.activity);
                
                if (selectedProject?.ticket_id_prefix && selectedActivity?.requires_ticket_id) {
                  const activityName = selectedActivity.name.toLowerCase();
                  const isTicketResolution = activityName.includes('ticket resolution') || 
                                            activityName.includes('am') || 
                                            activityName.includes('enh');
                  
                  if (isTicketResolution) {
                    const prefix = selectedProject.ticket_id_prefix;
                    const selectionStart = e.target.selectionStart;
                    const selectionEnd = e.target.selectionEnd;
                    
                    // Se la selezione include parte del prefix, sposta la selezione dopo il prefix
                    if (selectionStart < prefix.length) {
                      e.target.setSelectionRange(prefix.length, Math.max(selectionEnd, prefix.length));
                    }
                  }
                }
              }}
              required={formData.activity && activities.find(a => a.id === formData.activity)?.requires_ticket_id}
              sx={{ width: '100%', maxWidth: '100%' }}
            />
            <TextField
              name="effort"
              label="Effort"
              value={formData.effort}
              onChange={handleFormChange}
              required
              type="text"
              placeholder="e.g., 30, 60, 120"
              InputProps={{
                endAdornment: <InputAdornment position="end">min</InputAdornment>,
              }}
              inputProps={{
                pattern: "[0-9]+", // Allow digits only
                title: "Enter effort in minutes (e.g., 30, 60, 120)"
              }}
              sx={{ width: '100%', maxWidth: '100%' }}
            />
            <TextField
              name="description"
              label="Description"
              value={formData.description}
              onChange={handleFormChange}
              multiline
              rows={2}
              required={formData.activity && activities.find(a => a.id === formData.activity)?.requires_description}
              sx={{ width: '100%', maxWidth: '100%' }}
            />
            <TextField
              label="Date"
              value={formatDate(selectedDate)}
              disabled
              sx={{ width: '100%', maxWidth: '100%' }}
            />
            
            {/* Avviso per restrizioni temporali del progetto */}
            {formData.project && isDateBlockedForProject(selectedDate, formData.project) && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                 {getDateRestrictionMessage(formData.project)}
              </Alert>
            )}
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              {(() => {
                const selectedProject = projects.find(p => p.id === formData.project);
                const selectedActivity = activities.find(a => a.id === formData.activity);
                if (!selectedActivity) return null;
                const activityName = selectedActivity.name?.toLowerCase?.() || '';
                const isTicketResolution = activityName.includes('ticket resolution') || activityName.includes('am') || activityName.includes('enh');
                const parts = [];
                if (selectedProject?.ticket_id_prefix && isTicketResolution) {
                  parts.push(`Prefix "${selectedProject.ticket_id_prefix}" is locked. You can only add text after it.`);
                }
                if (selectedProject?.regex_desc) {
                  parts.push(selectedProject.regex_desc);
                } else if (selectedProject?.regex && isTicketResolution) {
                  parts.push('This project enforces a specific Ticket ID format.');
                }
                const text = parts.join(' ');
                return (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 'bold', display: 'block' }}>
                    {text || 'No specific Ticket ID requirements for this project/activity combination.'}
                  </Typography>
                );
              })()}
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 'bold', display: 'block', mt: 1 }}>
                Note: Effort input must be exclusively in minutes (enter only the number, e.g., 30, 60, 120).
              </Typography>
            </Box>
            {formError && <Alert severity="error">{formError}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveWorklog} 
            variant="contained" 
            color="primary" 
            disabled={saving || (formData.project && isDateBlockedForProject(selectedDate, formData.project))}
          >
            {saving ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openMultiDialog} onClose={handleCloseMultiDialog} maxWidth="lg" fullWidth>
        <DialogTitle>Add Worklogs for {formatDate(selectedDate)}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 2 }}>
            <Autocomplete
              options={projects}
              getOptionLabel={(option) => `${option.name}${option.customer?.name ? ` - ${option.customer.name}` : ''}`}
              value={projects.find(p => p.id === multiCommon.project) || null}
              onChange={(event, newValue) => {
                const newProjectId = newValue ? newValue.id : '';
                const ticketIdPrefix = handleMultiTicketIdPrefix(newProjectId, multiCommon.activity);
                setMultiCommon(prev => ({
                  ...prev,
                  project: newProjectId
                }));
                // Aggiorna tutti i ticket_id delle righe con il prefix
                if (ticketIdPrefix) {
                  const updatedRows = multiRows.map(row => ({
                    ...row,
                    ticket_id: ticketIdPrefix
                  }));
                  setMultiRows(updatedRows);
                }
                // Carica activity consentite per il progetto (multi dialog)
                (async () => {
                  const ids = await fetchAllowedActivityIdsForProject(newProjectId);
                  setAllowedActivitiesByProject(prev => ({ ...prev, multi: ids }));
                  if (ids.length && multiCommon.activity && !ids.includes(multiCommon.activity)) {
                    setMultiCommon(prev => ({ ...prev, activity: '' }));
                  }
                })();
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Project"
                  required
                  name="project"
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body2">{option.name}</Typography>
                    {option.customer?.name && (
                      <Typography variant="caption" color="textSecondary">
                        {option.customer.name}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              sx={{ flex: { xs: '1 1 100%', md: '1 1 0' }, width: { xs: '100%', md: 'auto' } }}
            />
            <Autocomplete
              options={activities}
              getOptionLabel={(option) => option.name || ''}
              value={activities.find(a => a.id === multiCommon.activity) || null}
              onChange={(event, newValue) => {
                const newActivityId = newValue ? newValue.id : '';
                const oldTicketIdPrefix = handleMultiTicketIdPrefix(multiCommon.project, multiCommon.activity);
                const newTicketIdPrefix = handleMultiTicketIdPrefix(multiCommon.project, newActivityId);
                setMultiCommon(prev => ({
                  ...prev,
                  activity: newActivityId
                }));
                
                // Gestione del ticket_id in base alla nuova attività
                if (newTicketIdPrefix) {
                  // La nuova attività richiede ticket_id: aggiorna tutte le righe con il nuovo prefix
                  const updatedRows = multiRows.map(row => ({
                    ...row,
                    ticket_id: newTicketIdPrefix
                  }));
                  setMultiRows(updatedRows);
                } else {
                  // La nuova attività NON richiede ticket_id: svuota solo se contiene SOLO il vecchio prefix
                  const updatedRows = multiRows.map(row => {
                    const currentTicketId = (row.ticket_id || '').trim();
                    const oldPrefix = (oldTicketIdPrefix || '').trim();
                    
                    // Se il ticket_id contiene SOLO il prefix (senza altro testo), svuotalo
                    if (oldPrefix && currentTicketId === oldPrefix) {
                      return { ...row, ticket_id: '' };
                    }
                    // Altrimenti mantieni il valore corrente (l'utente potrebbe aver aggiunto del testo)
                    return row;
                  });
                  setMultiRows(updatedRows);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Activity"
                  required
                  name="activity"
                />
              )}
              filterOptions={(opts) => {
                const allowed = allowedActivitiesByProject.multi;
                if (!multiCommon.project || !Array.isArray(allowed) || allowed.length === 0) return opts;
                return opts.filter(o => allowed.includes(o.id));
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              sx={{ flex: { xs: '1 1 100%', md: '1 1 0' }, width: { xs: '100%', md: 'auto' } }}
            />
            <Autocomplete
              options={effortTypes}
              getOptionLabel={(option) => option.name || ''}
              value={effortTypes.find(et => et.id === multiCommon.effort_type) || null}
              onChange={(event, newValue) => {
                setMultiCommon(prev => ({
                  ...prev,
                  effort_type: newValue ? newValue.id : ''
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Effort Type"
                  required
                  name="effort_type"
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              sx={{ flex: { xs: '1 1 100%', md: '1 1 0' }, width: { xs: '100%', md: 'auto' } }}
            />
          </Box>
          {multiRows.map((row, idx) => (
            <Box key={idx} sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 1 }}>
              <TextField
                name="ticket_id"
                label="Ticket ID"
                value={row.ticket_id}
                onChange={e => {
                  const selectedProject = projects.find(p => p.id === multiCommon.project);
                  const selectedActivity = activities.find(a => a.id === multiCommon.activity);
                  
                  if (selectedProject?.ticket_id_prefix && selectedActivity?.requires_ticket_id) {
                    const activityName = selectedActivity.name.toLowerCase();
                    const isTicketResolution = activityName.includes('ticket resolution') || 
                                              activityName.includes('am') || 
                                              activityName.includes('enh');
                    
                    if (isTicketResolution) {
                      const prefix = selectedProject.ticket_id_prefix;
                      const newValue = e.target.value;
                      
                      // Se il nuovo valore non inizia con il prefix, mantieni il prefix
                      if (!newValue.startsWith(prefix)) {
                        e.target.value = prefix;
                      }
                    }
                  }
                  
                  handleMultiRowChange(idx, e);
                }}
                onKeyDown={(e) => {
                  const selectedProject = projects.find(p => p.id === multiCommon.project);
                  const selectedActivity = activities.find(a => a.id === multiCommon.activity);
                  
                  if (selectedProject?.ticket_id_prefix && selectedActivity?.requires_ticket_id) {
                    const activityName = selectedActivity.name.toLowerCase();
                    const isTicketResolution = activityName.includes('ticket resolution') || 
                                              activityName.includes('am') || 
                                              activityName.includes('enh');
                    
                    if (isTicketResolution) {
                      const prefix = selectedProject.ticket_id_prefix;
                      const currentValue = e.target.value;
                      const cursorPosition = e.target.selectionStart;
                      
                      // Se l'utente prova a cancellare il prefix (Backspace o Delete)
                      if ((e.key === 'Backspace' || e.key === 'Delete') && cursorPosition < prefix.length) {
                        e.preventDefault();
                      }
                    }
                  }
                }}
                onSelect={(e) => {
                  const selectedProject = projects.find(p => p.id === multiCommon.project);
                  const selectedActivity = activities.find(a => a.id === multiCommon.activity);
                  
                  if (selectedProject?.ticket_id_prefix && selectedActivity?.requires_ticket_id) {
                    const activityName = selectedActivity.name.toLowerCase();
                    const isTicketResolution = activityName.includes('ticket resolution') || 
                                              activityName.includes('am') || 
                                              activityName.includes('enh');
                    
                    if (isTicketResolution) {
                      const prefix = selectedProject.ticket_id_prefix;
                      const selectionStart = e.target.selectionStart;
                      const selectionEnd = e.target.selectionEnd;
                      
                      // Se la selezione include parte del prefix, sposta la selezione dopo il prefix
                      if (selectionStart < prefix.length) {
                        e.target.setSelectionRange(prefix.length, Math.max(selectionEnd, prefix.length));
                      }
                    }
                  }
                }}
                required={multiCommon.activity && activities.find(a => a.id === multiCommon.activity)?.requires_ticket_id}
                sx={{ width: { xs: '100%', md: 180 }, maxWidth: { xs: '100%', md: 180 }, flex: { xs: '1 1 100%', md: '0 0 180px' } }}
              />
              <TextField
                name="effort"
                label="Effort"
                value={row.effort}
                onChange={e => handleMultiRowChange(idx, e)}
                required
                type="text"
                placeholder="e.g., 30, 60"
                InputProps={{
                  endAdornment: <InputAdornment position="end">min</InputAdornment>,
                }}
                inputProps={{
                  pattern: "[0-9]+", // Allow digits only
                  title: "Enter effort in minutes (e.g., 30, 60, 120)"
                }}
                sx={{ width: { xs: '100%', md: 126 }, maxWidth: { xs: '100%', md: 126 }, flex: { xs: '1 1 100%', md: '0 0 126px' } }}
              />
              <TextField
                name="description"
                label="Description"
                value={row.description}
                onChange={e => handleMultiRowChange(idx, e)}
                fullWidth
                multiline
                rows={1}
                required={multiCommon.activity && activities.find(a => a.id === multiCommon.activity)?.requires_description}
                sx={{ width: '100%', maxWidth: '100%' }}
              />
              <IconButton onClick={() => handleRemoveMultiRow(idx)} disabled={multiRows.length === 1}>
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
          <Button onClick={handleAddMultiRow} sx={{ mt: 1, mb: 2 }} variant="outlined">Add Row</Button>
          
          {/* Avviso per restrizioni temporali del progetto nel dialog multiplo */}
          {multiCommon.project && isDateBlockedForProject(selectedDate, multiCommon.project) && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {getDateRestrictionMessage(multiCommon.project)}
            </Alert>
          )}
          {/* Helper dinamico per Ticket ID (multiplo) sopra la nota minuti */}
          {(() => {
            const selectedProject = projects.find(p => p.id === multiCommon.project);
            const selectedActivity = activities.find(a => a.id === multiCommon.activity);
            if (!selectedActivity) return null;
            const activityName = selectedActivity.name?.toLowerCase?.() || '';
            const isTicketResolution = activityName.includes('ticket resolution') || activityName.includes('am') || activityName.includes('enh');
            const parts = [];
            if (selectedProject?.ticket_id_prefix && isTicketResolution) {
              parts.push(`Prefix "${selectedProject.ticket_id_prefix}" is locked.`);
            }
            if (selectedProject?.regex && isTicketResolution) {
              if (selectedProject?.regex_desc) {
                parts.push(selectedProject.regex_desc);
              } else {
                parts.push('This project enforces a specific Ticket ID format.');
              }
            }
            const text = parts.join(' ');

          })()}
          
          <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            {(() => {
              const selectedProject = projects.find(p => p.id === multiCommon.project);
              const selectedActivity = activities.find(a => a.id === multiCommon.activity);
              if (!selectedActivity) return null;
              const activityName = selectedActivity.name?.toLowerCase?.() || '';
              const isTicketResolution = activityName.includes('ticket resolution') || activityName.includes('am') || activityName.includes('enh');
              const parts = [];
              if (selectedProject?.ticket_id_prefix && isTicketResolution) {
                parts.push(`Prefix "${selectedProject.ticket_id_prefix}" is locked.`);
              }
              if (selectedProject?.regex_desc) {
                parts.push(selectedProject.regex_desc);
              } else if (selectedProject?.regex && isTicketResolution) {
                parts.push('This project enforces a specific Ticket ID format.');
              }
              const text = parts.join(' ');
              return (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 'bold', display: 'block' }}>
                  {text || 'No specific Ticket ID requirements for this project/activity combination.'}
                </Typography>
              );
            })()}
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 'bold', display: 'block', mt: 1 }}>
              Note: Effort input must be EXCLUSIVELY in minutes (enter only the number, e.g., 30, 60, 120).
            </Typography>
          </Box>
          {multiError && <Alert severity="error">{multiError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMultiDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveMultiWorklogs} 
            variant="contained" 
            color="primary" 
            disabled={multiSaving || (multiCommon.project && isDateBlockedForProject(selectedDate, multiCommon.project))}
          >
            {multiSaving ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialog Assenze */}
      <Dialog open={openAbsenceDialog} onClose={() => setOpenAbsenceDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventBusyIcon color="warning" />
          Add Absence Days
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Click on the days to select/deselect. You can select multiple days across months.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
            <ToggleButtonGroup
              value={absenceFullDay}
              exclusive
              onChange={(e, val) => { if (val !== null) setAbsenceFullDay(val); }}
              size="small"
            >
              <ToggleButton value={true} sx={{ textTransform: 'none', px: 2, '&.Mui-selected': { bgcolor: '#ffcdd2', color: '#c62828' } }}>
                All Day
              </ToggleButton>
              <ToggleButton value={false} sx={{ textTransform: 'none', px: 2, '&.Mui-selected': { bgcolor: '#fff9c4', color: '#f57f17' } }}>
                Partial
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <StaticDatePicker
              displayStaticWrapperAs="desktop"
              value={absDialogNavDate}
              onChange={(newDate) => { if (newDate) setAbsDialogNavDate(newDate); }}
              onMonthChange={(newMonth) => { if (newMonth) setAbsDialogNavDate(newMonth); }}
              showDaysOutsideCurrentMonth={false}
              renderInput={(params) => <TextField {...params} />}
              renderDay={(day, _value, DayProps) => {
                const dateStr = day
                  ? `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`
                  : '';
                const isSelected = selectedAbsenceDates.has(dateStr);
                const existingEntry = dialogAbsenceDays.find(v => v.date === dateStr);
                const isExisting = !!existingEntry;
                const isMarkedForRemoval = absenceDatesToRemove.has(dateStr);
                const existingIsFullDay = existingEntry?.full_day;
                return (
                  <Box
                    key={dateStr}
                    sx={{ position: 'relative', height: 40, cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!dateStr) return;
                      if (isExisting) {
                        setAbsenceDatesToRemove(prev => {
                          const next = new Set(prev);
                          if (next.has(dateStr)) next.delete(dateStr);
                          else next.add(dateStr);
                          return next;
                        });
                      } else {
                        setSelectedAbsenceDates(prev => {
                          const next = new Set(prev);
                          if (next.has(dateStr)) next.delete(dateStr);
                          else next.add(dateStr);
                          return next;
                        });
                      }
                    }}
                  >
                    <PickersDay
                      {...DayProps}
                      selected={false}
                      day={day}
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50% !important',
                        ...(isExisting && isMarkedForRemoval ? {
                          border: '2px dashed #ef5350 !important',
                          backgroundColor: '#ffebee !important',
                          color: '#c62828 !important',
                          fontWeight: 700,
                          textDecoration: 'line-through',
                        } : isExisting ? {
                          border: `2px solid ${existingIsFullDay ? '#e53935' : '#f9a825'} !important`,
                          backgroundColor: `${existingIsFullDay ? '#ffcdd2' : '#fff9c4'} !important`,
                          color: `${existingIsFullDay ? '#b71c1c' : '#f57f17'} !important`,
                          fontWeight: 700,
                        } : {
                          border: 'none !important',
                          backgroundColor: isSelected ? (absenceFullDay ? '#ef9a9a !important' : '#fff176 !important') : 'transparent !important',
                          color: isSelected ? (absenceFullDay ? '#b71c1c !important' : '#f57f17 !important') : 'inherit !important',
                        }),
                        pointerEvents: 'none',
                        '&.MuiPickersDay-today': {
                          border: isExisting ? `2px solid ${existingIsFullDay ? '#e53935' : '#f9a825'} !important` : '1.2px solid #248b7e !important',
                        },
                      }}
                    />
                  </Box>
                );
              }}
              sx={{
                width: '100%',
                '& .MuiPickersDay-root': { border: 'none !important' },
              }}
            />
          </LocalizationProvider>
          {selectedAbsenceDates.size > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: absenceFullDay ? '#c62828' : '#f57f17', mb: 0.5, display: 'block' }}>Add ({absenceFullDay ? 'Full Day' : 'Partial'}):</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {Array.from(selectedAbsenceDates).sort().map(d => (
                  <Chip
                    key={d}
                    label={parseLocalDate(d).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })}
                    size="small"
                    color={absenceFullDay ? "error" : "warning"}
                    variant="outlined"
                    onDelete={() => setSelectedAbsenceDates(prev => { const n = new Set(prev); n.delete(d); return n; })}
                  />
                ))}
              </Box>
            </Box>
          )}
          {absenceDatesToRemove.size > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#c62828', mb: 0.5, display: 'block' }}>Remove:</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {Array.from(absenceDatesToRemove).sort().map(d => (
                  <Chip
                    key={d}
                    label={parseLocalDate(d).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })}
                    size="small"
                    color="error"
                    variant="outlined"
                    onDelete={() => setAbsenceDatesToRemove(prev => { const n = new Set(prev); n.delete(d); return n; })}
                  />
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAbsenceDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color={absenceFullDay ? "error" : "warning"}
            onClick={handleSaveAbsences}
            disabled={absenceSaving || (selectedAbsenceDates.size === 0 && absenceDatesToRemove.size === 0)}
            startIcon={absenceSaving ? <CircularProgress size={16} /> : <EventBusyIcon />}
          >
            Save{selectedAbsenceDates.size > 0 ? ` +${selectedAbsenceDates.size}` : ''}{absenceDatesToRemove.size > 0 ? ` -${absenceDatesToRemove.size}` : ''}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar warning per worklog su giorno con assenza totale */}
      <Snackbar
        open={absenceWarningOpen}
        onClose={() => setAbsenceWarningOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setAbsenceWarningOpen(false)} severity="warning" sx={{ width: '100%' }}>
          This day is marked as a full-day absence. You can still add a worklog if needed.
        </Alert>
      </Snackbar>

      {/* Warning Dialog per effort superiore a 8 ore */}
      <Dialog open={showWarningDialog} onClose={handleWarningCancel}>
        <DialogTitle sx={{ color: '#f57c00' }}>
          Warning: Daily Effort Exceeds 8 Hours
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            The total daily effort for {formatDate(selectedDate)} will exceed 8 hours if you continue with this {pendingWorklogData?.isMulti ? 'worklog group' : 'worklog'}.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to proceed with the {pendingWorklogData?.isMulti ? 'insertion' : (pendingWorklogData?.isEditing ? 'update' : 'insertion')}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleWarningCancel} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleWarningConfirm} 
            variant="contained" 
            color="warning"
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : 'Continue'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}